import { loadPersonalSyncStatus } from './contact_sync_personal_status';
import { rebuildPersonalContactsFromDexie, writePersonalMarksToLoki } from './contact_sync_personal_loki';
import { writeOrgsToLoki } from './contact_sync_enterprise_loki';
import { contactSearchDbName } from './util/const';
export async function ensureContactDataReady(ctx, coreSync, coordinator, options) {
    var _a;
    const { _account } = options;
    const dbAccount = coordinator.getDbAccount(_account);
    const [hasPersonalData, hasEnterpriseData, hasOrgData] = await Promise.all([
        checkLokiHasPersonalData(ctx, dbAccount),
        coordinator.checkLokiHasEnterpriseData(_account),
        coordinator.checkLokiHasOrgData(_account),
    ]);
    console.log(`[contact_sync_ensure_ready] Phase1 loki check: personal=${hasPersonalData}, enterprise=${hasEnterpriseData}, org=${hasOrgData}, account=${_account}`);
    if (hasPersonalData && hasEnterpriseData && hasOrgData) {
        console.log('[contact_sync_ensure_ready] loki already has all data, skip rebuild.');
        return;
    }
    let needsHttp = false;
    if (!hasPersonalData) {
        const personalStatus = await loadPersonalSyncStatus(ctx.storeApi);
        console.log(`[contact_sync_ensure_ready] personal status=${(_a = personalStatus === null || personalStatus === void 0 ? void 0 : personalStatus.status) !== null && _a !== void 0 ? _a : 'null'}, account=${_account}`);
        console.log('[contact_sync_ensure_ready] rebuilding personal contacts from dexie...');
        await rebuildPersonalContactsFromDexie({
            lokiApi: ctx.lokiApi,
            dbApi: ctx.dbApi,
            contactUtil: ctx.contactUtil,
            _dbAccount: dbAccount,
        });
        await ensurePersonalOrgInLoki(ctx, dbAccount);
        const hasDataAfterRebuild = await checkLokiHasPersonalData(ctx, dbAccount);
        console.log(`[contact_sync_ensure_ready] personal rebuild done, hasData=${hasDataAfterRebuild}`);
        if (hasDataAfterRebuild) {
            ctx.contactDB.sendContactNotify({
                syncStatus: { personal: true, personalOrg: true },
                _account,
            });
        }
        else {
            console.warn('[contact_sync_ensure_ready] personal rebuild yielded no data, fallback to HTTP');
            needsHttp = true;
        }
    }
    if (!hasEnterpriseData || !hasOrgData) {
        const enterpriseConfig = await loadEnterpriseConfigFromStore(ctx, _account);
        console.log(`[contact_sync_ensure_ready] enterprise config: ${enterpriseConfig ? `hasAllContacts=${enterpriseConfig.hasAllContacts}` : 'not found'}`);
        if (!enterpriseConfig) {
            needsHttp = true;
        }
        else {
            const dbName = ctx.contactUtil.contactDbName;
            if (!hasOrgData) {
                await coordinator.rebuildOrgsFromDexie(dbName, dbAccount);
            }
            if (!hasEnterpriseData) {
                await coordinator.rebuildContactsFromDexie(dbName, dbAccount);
            }
            console.log('[contact_sync_ensure_ready] enterprise rebuild from dexie done, sending notify');
            ctx.contactDB.sendContactNotify({
                syncStatus: { enterprise: true, org: true },
                _account,
            });
        }
    }
    if (needsHttp && options.allowHttpSync) {
        console.log('[contact_sync_ensure_ready] Phase3 starting one-shot HTTP sync, enablePolling=false');
        await coreSync.doSyncAction({
            _account,
            isMainAccount: options.isMainAccount,
            force: false,
            enablePolling: false,
            trigger: 'nonMainTab',
        });
        console.log('[contact_sync_ensure_ready] Phase3 HTTP sync done');
    }
    else if (needsHttp && !options.allowHttpSync) {
        console.log('[contact_sync_ensure_ready] Phase3 skipped: allowHttpSync=false, account=' + _account);
    }
}
async function checkLokiHasPersonalData(ctx, dbAccount) {
    try {
        const results = await ctx.lokiApi.getByRangeCondition({
            dbName: contactSearchDbName,
            tableName: 'contact',
            adCondition: {
                type: 'equals',
                field: 'type',
                args: ['personal'],
            },
            count: 1,
            _dbAccount: dbAccount,
        });
        return Array.isArray(results) && results.length > 0;
    }
    catch (ex) {
        console.error('[contact_sync_ensure_ready] checkLokiHasPersonalData error', ex);
        return false;
    }
}
async function ensurePersonalOrgInLoki(ctx, dbAccount) {
    try {
        const records = await ctx.lokiApi.getByRangeCondition({
            dbName: contactSearchDbName,
            tableName: 'org',
            adCondition: {
                type: 'equals',
                field: 'type',
                args: [2001],
            },
            count: 1,
            _dbAccount: dbAccount,
        });
        if (Array.isArray(records) && records.length > 0) {
            return;
        }
        const [orgList, markList] = await Promise.all([
            ctx.dbApi.getByEqCondition({
                dbName: ctx.contactUtil.contactDbName,
                tableName: ctx.contactUtil.orgTable,
                query: { type: 2001 },
                _dbAccount: dbAccount,
            }),
            ctx.dbApi.getByEqCondition({
                dbName: ctx.contactUtil.contactDbName,
                tableName: ctx.contactUtil.personalMarkTable,
                query: { type: 2 },
                _dbAccount: dbAccount,
            }),
        ]);
        await Promise.all([
            writeOrgsToLoki(ctx.lokiApi, orgList, dbAccount),
            writePersonalMarksToLoki(ctx.lokiApi, markList, dbAccount),
        ]);
        console.log(`[contact_sync_ensure_ready] ensurePersonalOrgInLoki done. orgs=${orgList.length}, marks=${markList.length}`);
    }
    catch (err) {
        console.warn('[contact_sync_ensure_ready] ensurePersonalOrgInLoki error:', err);
    }
}
async function loadEnterpriseConfigFromStore(ctx, _account) {
    var _a;
    const domain = (_a = ctx.systemApi.getCurrentUser(_account)) === null || _a === void 0 ? void 0 : _a.domain;
    if (!domain) {
        console.warn(`[contact_sync_ensure_ready] loadEnterpriseConfigFromStore: no domain found for account=${_account}`);
        return null;
    }
    const key = `enterprise_contacts_config__${domain}`;
    try {
        const result = await ctx.storeApi.get(key);
        if (result.suc && result.data) {
            const config = JSON.parse(result.data);
            console.log('[contact_sync_ensure_ready] loadEnterpriseConfigFromStore success', { domain, hasAllContacts: config.hasAllContacts });
            return config;
        }
        console.log(`[contact_sync_ensure_ready] loadEnterpriseConfigFromStore: no config in store for domain=${domain}`);
        return null;
    }
    catch (ex) {
        console.error('[contact_sync_ensure_ready] loadEnterpriseConfigFromStore failed', ex);
        return null;
    }
}
